import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Building2,
  Phone,
  MapPin,
  Store,
  User,
  Receipt,
  Calendar,
  SearchIcon,
  CircleX,
  ChevronUp,
} from "lucide-react";
import {
  TextField,
  CircularProgress,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  useFetchCustomerRequestByIdQuery,
  useLazyFetchVendorStoreQuery,
  useFetchAllBrandsQuery,
  useRejectCustomerRequestMutation,
  useApproveCustomerRequestMutation,
  useChangeToApproveMutation,
} from "../../../services/baseQuery";
import BrandCheckbox from "./Components/BrandCheckbox";
import { toast } from "react-hot-toast";

const COOLDOWN_OPTIONS = {
  NO_COOLDOWN: {
    label: "Allow immediate reconnection",
    hours: 0,
    description: "Customer can submit a new request right away",
  },
  ONE_HOUR: {
    label: "Wait for 1 hour",
    hours: 1,
    description:
      "Customer must wait for 1 hour before submitting a new request",
  },
  ONE_DAY: {
    label: "Wait for 24 hours",
    hours: 24,
    description: "Customer must wait for 1 day before submitting a new request",
  },
  ONE_WEEK: {
    label: "Wait for 1 week",
    hours: 168,
    description:
      "Customer must wait for 7 days before submitting a new request",
  },
  ONE_MONTH: {
    label: "Wait for 1 month",
    hours: 720,
    description:
      "Customer must wait for 30 days before submitting a new request",
  },
  CUSTOM: {
    label: "Set custom date",
    hours: null,
    description:
      "Choose a specific date when customer can submit a new request",
  },
};

const PREDEFINED_REJECT_REASONS = [
  {
    id: 1,
    title: "Invalid Documents",
    reason: "Invalid business documents provided.",
    description: "The submitted business documents are invalid or expired",
  },
  {
    id: 2,
    title: "Incomplete Information",
    reason: "Incomplete business information.",
    description: "Required business details are missing or incomplete",
  },
  {
    id: 3,
    title: "Location Issue",
    reason: "Business location not serviceable.",
    description: "The business location is outside our service area",
  },
];

const CustomerRequestDetails = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();

  // Add order validation state
  const [orderValidation, setOrderValidation] = useState({
    validationType: "USER_SPECIFIC",
    minOrderQty: "",
    minOrderValue: "",
    validationCondition: "ANY_ONE",
  });

  const [
    useDefaultOrderValidationSettings,
    setUseDefaultOrderValidationSettings,
  ] = useState(false);
  const [brandVisibility, setBrandVisibility] = useState({
    visibilityType: "USER_SPECIFIC",
    removedBrands: [],
  });
  const [creditLimit, setCreditLimit] = useState("");

  // Add rejection details state (already exists)
  const [rejectionDetails, setRejectionDetails] = useState({
    reason: "",
    allowReconnection: false,
    cooldown: {
      type: "NO_COOLDOWN",
      hours: 24,
      timestamp: null,
    },
  });

  // Add vendor store query
  const [
    triggerFetchVendorStore,
    { isFetching: fetchingVendorStore, data: vendorStoreData },
  ] = useLazyFetchVendorStoreQuery();

  // Add these states for brand visibility
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectAllBrands, setSelectAllBrands] = useState(false);
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [notes, setNotes] = useState("");

  // Add brands query
  const {
    isLoading: fetchBrandsLoading,
    data: brandsResponseData,
    error: fetchBrandsError,
    isError: isFetchBrandsError,
  } = useFetchAllBrandsQuery();

  // Change expandedSection to handle only approve/reject sections
  const [expandedSection, setExpandedSection] = useState(null);

  // Add reject mutation
  const [rejectRequest, { isLoading: isRejecting }] =
    useRejectCustomerRequestMutation();

  // First, add a state to control the status change form visibility
  const [showApproveForm, setShowApproveForm] = useState(false);

  // Add approve mutation
  const [approveRequest, { isLoading: isApproving }] =
    useApproveCustomerRequestMutation();

  // Add the new mutation hook
  const [changeToApprove, { isLoading: isChangingToApprove }] =
    useChangeToApproveMutation();

  // Add useEffect for vendor store data
  useEffect(() => {
    if (vendorStoreData?.success && useDefaultOrderValidationSettings) {
      const { minOrderQty, minOrderValue, validationCondition } =
        vendorStoreData.data.store.orderValidation;

      setOrderValidation(prev => ({
        ...prev,
        validationType: "COMPANY_SETTINGS",
        minOrderQty: minOrderQty || "",
        minOrderValue: minOrderValue || "",
        validationCondition: validationCondition || "",
      }));
    }
  }, [vendorStoreData, useDefaultOrderValidationSettings]);

  // Add handler for default settings
  const handleDefaultSettingsChange = event => {
    const checked = event.target.checked;
    setUseDefaultOrderValidationSettings(checked);

    if (checked) {
      triggerFetchVendorStore();
    } else {
      setOrderValidation(prev => ({
        ...prev,
        validationType: "USER_SPECIFIC",
        minOrderQty: "",
        minOrderValue: "",
        validationCondition: "",
      }));
    }
  };

  const { data: requestData, isLoading } =
    useFetchCustomerRequestByIdQuery(requestId);

  console.log(requestData, "<<< request data");

  const formatAddress = address => {
    if (!address) return "-";
    const { localAddress, locality, city_district, state, pincode } = address;
    return `${localAddress}, ${locality}, ${city_district}, ${state} - ${pincode}`;
  };

  const handleReject = async () => {
    if (!rejectionDetails.reason) return;

    try {
      await rejectRequest({
        customerId: requestData?.data?.customerRequest?.customerStore?._id,
        rejectionDetails,
      }).unwrap();

      // Show success message
      toast.success("Request rejected successfully");

      // Optionally navigate back
      navigate(-1);
    } catch (error) {
      // Show error message
      toast.error(error.data?.message || "Failed to reject request");
    }
  };

  const handleChangeToApprove = async () => {
    try {
      const vcConfigSettings = {
        creditLimit: Number(creditLimit),
        brandVisibility: {
          visibilityType: selectAllBrands
            ? "ALL_COMPANY_BRANDS"
            : "USER_SPECIFIC",
          removedBrands: selectAllBrands
            ? []
            : brandsResponseData?.data?.brands
                .filter(brand => !selectedBrands.includes(brand._id))
                .map(brand => brand._id),
        },
        orderValidation: {
          validationType: useDefaultOrderValidationSettings
            ? "COMPANY_SETTINGS"
            : "USER_SPECIFIC",
          ...(useDefaultOrderValidationSettings
            ? {}
            : {
                minOrderQty: Number(orderValidation.minOrderQty),
                minOrderValue: Number(orderValidation.minOrderValue),
                validationCondition: orderValidation.validationCondition,
              }),
        },
      };

      await changeToApprove({
        customerStoreId: requestData?.data?.customerRequest?.customerStore?._id,
        vcConfigSettings,
        additionalNotes: notes,
      }).unwrap();

      toast.success("Request status changed to approved successfully");
      navigate(-1);
    } catch (error) {
      toast.error(error.data?.message || "Failed to change request status");
    }
  };

  const getStatusBadge = status => {
    switch (status) {
      case "APPROVED":
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full">
            <CheckCircle2 size={16} />
            <span className="font-medium">Approved</span>
          </div>
        );
      case "REJECTED":
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-full">
            <XCircle size={16} />
            <span className="font-medium">Rejected</span>
          </div>
        );
      default:
        return (
          <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
    }
  };

  const renderCustomerInfo = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">
          Request Information
        </h2>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <InfoItem
            icon={<Building2 size={18} />}
            label="Business Name"
            value={
              requestData?.data?.customerRequest?.customerStore?.businessName
            }
          />
          <InfoItem
            icon={<Phone size={18} />}
            label="Phone Number"
            value={
              requestData?.data?.customerRequest?.customerStore
                ?.registeredPhoneNo
            }
          />
          <InfoItem
            icon={<User size={18} />}
            label="Owner Name"
            value={requestData?.data?.customerRequest?.customerStore?.ownerName}
          />
          <InfoItem
            icon={<Store size={18} />}
            label="Store Type"
            value={requestData?.data?.customerRequest?.customerStore?.storeType}
          />
          <InfoItem
            icon={<Receipt size={18} />}
            label="GSTIN"
            value={requestData?.data?.customerRequest?.customerStore?.GSTIN}
          />
          <InfoItem
            icon={<Calendar size={18} />}
            label="Request Date"
            value={new Date(
              requestData?.data?.customerRequest?.createdAt,
            ).toLocaleDateString()}
          />
          <div className="col-span-2">
            <InfoItem
              icon={<MapPin size={18} />}
              label="Shop Address"
              value={formatAddress(
                requestData?.data?.customerRequest?.customerStore?.shopAddress,
              )}
            />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">
              Request Type
            </h4>
            <div className="flex items-center gap-2">
              {requestData?.data?.customerRequest?.requestType ===
              "RECONNECTION" ? (
                <>
                  <span className="text-purple-700 bg-purple-50 px-2 py-1 rounded text-sm">
                    Reconnection Request
                  </span>
                  <span className="text-sm text-gray-500">
                    Previous connection was rejected
                  </span>
                </>
              ) : (
                <>
                  <span className="text-blue-700 bg-blue-50 px-2 py-1 rounded text-sm">
                    New Connection
                  </span>
                  <span className="text-sm text-gray-500">
                    First time connection request
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const InfoItem = ({ icon, label, value }) => (
    <div>
      <div className="flex items-center gap-2 text-gray-500 mb-1">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <p className="text-gray-900 font-medium">{value || "-"}</p>
    </div>
  );

  // Add brand handling functions
  const handleBrandSelection = brandId => {
    const allBrands = brandsResponseData?.data?.brands || [];

    setSelectedBrands(prev => {
      let newSelection;
      if (prev.includes(brandId)) {
        newSelection = prev.filter(id => id !== brandId);
        setBrandVisibility(prev => ({
          ...prev,
          visibilityType: "USER_SPECIFIC",
          removedBrands: allBrands
            .map(brand => brand._id)
            .filter(id => !newSelection.includes(id)),
        }));
        setSelectAllBrands(false);
      } else {
        newSelection = [...prev, brandId];
        setBrandVisibility(prev => ({
          ...prev,
          visibilityType: "USER_SPECIFIC",
          removedBrands: allBrands
            .map(brand => brand._id)
            .filter(id => !newSelection.includes(id)),
        }));
        if (newSelection.length === allBrands.length) {
          setSelectAllBrands(true);
          setBrandVisibility(prev => ({
            ...prev,
            visibilityType: "ALL_COMPANY_BRANDS",
          }));
        }
      }
      return newSelection;
    });
  };

  const handleSelectAllBrands = event => {
    const checked = event.target.checked;
    setSelectAllBrands(checked);

    if (checked) {
      const allBrandIds =
        brandsResponseData?.data?.brands?.map(brand => brand._id) || [];
      setSelectedBrands(allBrandIds);
      setBrandVisibility({
        visibilityType: "ALL_COMPANY_BRANDS",
      });
    } else {
      setSelectedBrands([]);
      setBrandVisibility({
        visibilityType: "USER_SPECIFIC",
        removedBrands:
          brandsResponseData?.data?.brands?.map(brand => brand._id) || [],
      });
    }
  };

  const handleUnselectAllBrands = () => {
    setSelectedBrands([]);
    setSelectAllBrands(false);
    setBrandVisibility({
      visibilityType: "USER_SPECIFIC",
      removedBrands:
        brandsResponseData?.data?.brands?.map(brand => brand._id) || [],
    });
  };

  const getFilteredBrands = () => {
    if (!brandsResponseData?.success) return { brands: [], isEmpty: true };

    const allBrands = brandsResponseData.data.brands;
    const searchFiltered = searchQuery
      ? allBrands.filter(brand =>
          brand.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : allBrands;

    const filteredBrands =
      {
        SELECTED: searchFiltered.filter(brand =>
          selectedBrands.includes(brand._id),
        ),
        UNSELECTED: searchFiltered.filter(
          brand => !selectedBrands.includes(brand._id),
        ),
        ALL: searchFiltered,
      }[brandFilter] || searchFiltered;

    return {
      brands: filteredBrands,
      isEmpty: filteredBrands.length === 0,
    };
  };

  const renderSectionHeader = (id, title, subtitle) => (
    <div
      onClick={() => setExpandedSection(expandedSection === id ? null : id)}
      className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
      <div>
        <h3
          className={`text-lg font-medium mb-1 ${
            id === "approve" ? "text-[#5652B0]" : "text-red-600"
          }`}>
          {title}
        </h3>
        <p className="text-sm text-gray-600">{subtitle}</p>
      </div>
      {expandedSection === id ? (
        <ChevronUp size={20} className="text-gray-400" />
      ) : (
        <ChevronUp size={20} className="text-gray-400" />
      )}
    </div>
  );

  const renderPendingActions = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">Action Required</h2>
      </div>

      <div className="divide-y">
        {/* Approval Section */}
        <div>
          {renderSectionHeader(
            "approve",
            "Approval Settings",
            "Configure these settings to approve the request",
          )}
          {expandedSection === "approve" && (
            <div className="p-6 space-y-6">
              {/* Credit Limit and Order Validation */}
              <div className="grid grid-cols-2 gap-6">
                {/* Credit Limit */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Credit Limit
                  </h4>
                  <TextField
                    label="Set Credit Limit"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={creditLimit}
                    onChange={e => setCreditLimit(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </div>

                {/* Order Validation */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Order Validation
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Checkbox
                        checked={useDefaultOrderValidationSettings}
                        onChange={handleDefaultSettingsChange}
                        size="small"
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": { color: "#5652B0" },
                        }}
                      />
                      <span className="text-sm">
                        Use company default settings
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      <RadioGroup
                        value={orderValidation.validationCondition}
                        onChange={e =>
                          setOrderValidation(prev => ({
                            ...prev,
                            validationCondition: e.target.value,
                          }))
                        }
                        row
                        size="small"
                        disabled={useDefaultOrderValidationSettings}>
                        <FormControlLabel
                          value="ANY_ONE"
                          control={
                            <Radio
                              size="small"
                              disabled={useDefaultOrderValidationSettings}
                              sx={{
                                color: "#5652B0",
                                "&.Mui-checked": { color: "#5652B0" },
                              }}
                            />
                          }
                          label={<span className="text-sm">Any One</span>}
                        />
                        <FormControlLabel
                          value="BOTH"
                          control={
                            <Radio
                              size="small"
                              disabled={useDefaultOrderValidationSettings}
                              sx={{
                                color: "#5652B0",
                                "&.Mui-checked": { color: "#5652B0" },
                              }}
                            />
                          }
                          label={<span className="text-sm">Both</span>}
                        />
                      </RadioGroup>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <TextField
                        label="MOQ"
                        variant="outlined"
                        size="small"
                        type="number"
                        value={
                          useDefaultOrderValidationSettings
                            ? vendorStoreData?.data?.store?.orderValidation
                                ?.minOrderQty
                            : orderValidation.minOrderQty
                        }
                        onChange={e =>
                          setOrderValidation(prev => ({
                            ...prev,
                            minOrderQty: e.target.value,
                          }))
                        }
                        disabled={useDefaultOrderValidationSettings}
                        fullWidth
                        InputProps={{
                          readOnly: useDefaultOrderValidationSettings,
                        }}
                      />
                      <TextField
                        label="MOV"
                        variant="outlined"
                        size="small"
                        type="number"
                        value={
                          useDefaultOrderValidationSettings
                            ? vendorStoreData?.data?.store?.orderValidation
                                ?.minOrderValue
                            : orderValidation.minOrderValue
                        }
                        onChange={e =>
                          setOrderValidation(prev => ({
                            ...prev,
                            minOrderValue: e.target.value,
                          }))
                        }
                        disabled={useDefaultOrderValidationSettings}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                          readOnly: useDefaultOrderValidationSettings,
                        }}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Brand Visibility */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Brand Visibility
                </h4>
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-x-4">
                      <div className="flex items-center">
                        <Checkbox
                          checked={selectAllBrands}
                          onChange={handleSelectAllBrands}
                          sx={{
                            color: "#5652B0",
                            "&.Mui-checked": { color: "#5652B0" },
                          }}
                        />
                        <span className="text-sm font-medium">
                          Select all brands
                        </span>
                      </div>
                      {selectedBrands.length > 0 && (
                        <button
                          onClick={handleUnselectAllBrands}
                          className="text-sm border border-[#5652B0] text-[#5652B0] px-3 py-1 rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                          Unselect All
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-x-4">
                      <div className="flex gap-x-3">
                        {["ALL", "SELECTED", "UNSELECTED"].map(filter => (
                          <button
                            key={filter}
                            onClick={() => setBrandFilter(filter)}
                            className={`text-sm font-medium transition-colors ${
                              brandFilter === filter
                                ? "text-[#5652B0] underline"
                                : "text-gray-600 hover:text-[#5652B0]"
                            }`}>
                            View {filter.toLowerCase()}
                          </button>
                        ))}
                      </div>

                      <div className="relative">
                        <SearchIcon
                          className="absolute left-3 top-1/2 transform -translate-y-1/2"
                          size={16}
                          color="#83899F"
                        />
                        <input
                          type="text"
                          placeholder="Search brands..."
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          className="w-[200px] h-9 pl-9 pr-8 rounded-lg border text-sm focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] outline-none"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery("")}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full">
                            <CircleX size={14} color="#83899F" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-3 max-h-[200px] overflow-y-auto p-3 bg-gray-50 rounded-lg">
                    {fetchBrandsLoading ? (
                      <div className="col-span-4 flex items-center justify-center h-[100px]">
                        <CircularProgress size={32} sx={{ color: "#5652B0" }} />
                      </div>
                    ) : getFilteredBrands().isEmpty ? (
                      <div className="col-span-4 flex items-center justify-center h-[100px] text-gray-500">
                        No brands found
                      </div>
                    ) : (
                      getFilteredBrands().brands.map(brand => (
                        <BrandCheckbox
                          key={brand._id}
                          id={brand._id}
                          label={brand.name}
                          checked={selectedBrands.includes(brand._id)}
                          onChange={() => handleBrandSelection(brand._id)}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Additional Notes
                </h4>
                <TextField
                  multiline
                  rows={2}
                  placeholder="Add any additional notes or comments..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  fullWidth
                  size="small"
                />
              </div>

              <button
                onClick={handleApprove}
                disabled={
                  isApproving ||
                  !creditLimit ||
                  (!useDefaultOrderValidationSettings &&
                    (!orderValidation.minOrderQty ||
                      !orderValidation.minOrderValue))
                }
                className={`w-full px-4 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg font-medium transition-colors flex items-center justify-center gap-2
                  ${
                    isApproving ||
                    !creditLimit ||
                    (!useDefaultOrderValidationSettings &&
                      (!orderValidation.minOrderQty ||
                        !orderValidation.minOrderValue))
                      ? "opacity-50 cursor-not-allowed"
                      : "shadow-sm hover:shadow"
                  }`}>
                {isApproving ? (
                  <>
                    <CircularProgress size={16} sx={{ color: "white" }} />
                    <span>Approving Request...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} />
                    <span>Approve Request</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Rejection Section */}
        <div>
          {renderSectionHeader(
            "reject",
            "Rejection Settings",
            "Fill these details to reject the request",
          )}
          {expandedSection === "reject" && (
            <div className="p-6 space-y-6">
              {/* Rejection Reason */}
              <div className="grid grid-cols-2 gap-6">
                {/* Left side - Input field */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Rejection Reason
                  </h4>
                  <TextField
                    multiline
                    rows={4}
                    value={rejectionDetails.reason}
                    onChange={e =>
                      setRejectionDetails(prev => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    fullWidth
                    required
                    placeholder="Enter detailed reason for rejection..."
                    size="small"
                  />
                </div>

                {/* Right side - Predefined reasons */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Common Rejection Issues
                  </h4>
                  <div className="space-y-3">
                    {PREDEFINED_REJECT_REASONS.map(
                      ({ id, title, reason, description }) => (
                        <div
                          key={id}
                          onClick={() =>
                            setRejectionDetails(prev => ({
                              ...prev,
                              reason,
                            }))
                          }
                          className={`p-3 border rounded-lg cursor-pointer transition-all
                          ${
                            rejectionDetails.reason === reason
                              ? "border-[#5652B0] bg-[#5652B0]/5"
                              : "hover:border-gray-400"
                          }`}>
                          <div className="flex items-center gap-2">
                            <Radio
                              checked={rejectionDetails.reason === reason}
                              size="small"
                              sx={{
                                color: "#5652B0",
                                "&.Mui-checked": { color: "#5652B0" },
                              }}
                            />
                            <div>
                              <h5 className="font-medium text-gray-900">
                                {title}
                              </h5>
                              <p className="text-sm text-gray-500">
                                {description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    <div
                      className={`p-3 border rounded-lg cursor-pointer transition-all
                        ${
                          !PREDEFINED_REJECT_REASONS.find(
                            r => r.reason === rejectionDetails.reason,
                          )
                            ? "border-[#5652B0] bg-[#5652B0]/5"
                            : "hover:border-gray-400"
                        }`}>
                      <div className="flex items-center gap-2">
                        <Radio
                          checked={
                            !PREDEFINED_REJECT_REASONS.find(
                              r => r.reason === rejectionDetails.reason,
                            )
                          }
                          size="small"
                          sx={{
                            color: "#5652B0",
                            "&.Mui-checked": { color: "#5652B0" },
                          }}
                        />
                        <div>
                          <h5 className="font-medium text-gray-900">
                            Custom Reason
                          </h5>
                          <p className="text-sm text-gray-500">
                            Enter a custom rejection reason
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reconnection Settings */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={rejectionDetails.allowReconnection}
                    onChange={e =>
                      setRejectionDetails(prev => ({
                        ...prev,
                        allowReconnection: e.target.checked,
                      }))
                    }
                    size="small"
                  />
                  <span className="text-sm">Allow reconnection request</span>
                </div>

                {rejectionDetails.allowReconnection && (
                  <div className="pl-6 space-y-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <span className="font-medium">What is this for?</span>{" "}
                        When you allow reconnection, the customer can submit a
                        new request after the selected waiting period. This
                        gives them a chance to fix the issues and try again.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <h5 className="text-sm font-medium text-gray-700">
                        How long should the customer wait before submitting a
                        new request?
                      </h5>
                      <div className="space-y-3">
                        {Object.entries(COOLDOWN_OPTIONS).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              onClick={() => {
                                setRejectionDetails(prev => ({
                                  ...prev,
                                  cooldown: {
                                    type: key,
                                    hours: value.hours,
                                    timestamp: key === "CUSTOM" ? null : null,
                                  },
                                }));
                              }}
                              className={`p-3 border rounded-lg cursor-pointer transition-all
                              ${
                                rejectionDetails.cooldown.type === key
                                  ? "border-[#5652B0] bg-[#5652B0]/5"
                                  : "hover:border-gray-400"
                              }`}>
                              <div className="flex items-center gap-2">
                                <Radio
                                  checked={
                                    rejectionDetails.cooldown.type === key
                                  }
                                  size="small"
                                  sx={{
                                    color: "#5652B0",
                                    "&.Mui-checked": { color: "#5652B0" },
                                  }}
                                />
                                <div>
                                  <h6 className="font-medium text-gray-900">
                                    {value.label}
                                  </h6>
                                  <p className="text-sm text-gray-500">
                                    {value.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>

                    {rejectionDetails.cooldown.type === "CUSTOM" && (
                      <div className="pl-6 space-y-2">
                        <p className="text-sm text-gray-600">
                          Select the exact date and time when the customer can
                          submit a new request:
                        </p>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            label="Select date and time"
                            value={rejectionDetails.cooldown.timestamp}
                            onChange={newValue =>
                              setRejectionDetails(prev => ({
                                ...prev,
                                cooldown: {
                                  ...prev.cooldown,
                                  timestamp: newValue,
                                },
                              }))
                            }
                            slotProps={{ textField: { size: "small" } }}
                          />
                        </LocalizationProvider>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={handleReject}
                disabled={!rejectionDetails.reason || isRejecting}
                className={`w-full px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium transition-colors
                  ${
                    !rejectionDetails.reason || isRejecting
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}>
                {isRejecting ? (
                  <div className="flex items-center justify-center gap-2">
                    <CircularProgress size={16} />
                    <span>Rejecting Request...</span>
                  </div>
                ) : (
                  "Reject Request"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const formatActivityLog = activity => {
    const getActivityMessage = () => {
      switch (activity.activityType) {
        case "REQUEST_CREATED":
          return "Request created";
        case "REQUEST_REJECTED":
          return `Request rejected by ${activity.performedBy?.fullName}`;
        case "REQUEST_APPROVED":
          return `Request approved by ${activity.performedBy?.fullName}`;
        default:
          return activity.activityType.replace(/_/g, " ");
      }
    };

    const getActivityDetails = () => {
      switch (activity.activityType) {
        case "REQUEST_REJECTED":
          return (
            <div className="mt-2 ml-[140px] space-y-2">
              <p className="text-gray-600">
                <span className="text-gray-500">Reason:</span>{" "}
                {activity.metadata.rejectionReason}
              </p>
              {activity.metadata.reconnectionAllowed && (
                <p className="text-gray-600">
                  <span className="text-gray-500">Reconnection:</span> Allowed
                  after{" "}
                  {new Date(activity.metadata.cooldown.period).toLocaleString()}
                </p>
              )}
            </div>
          );
        case "REQUEST_CREATED":
          return (
            <p className="mt-2 ml-[140px] text-gray-600">
              Initiated by {activity.metadata.initiatedBy.type.toLowerCase()}
            </p>
          );
        default:
          return null;
      }
    };

    return (
      <div key={activity._id} className="px-6 py-4 border-b border-gray-200 ">
        <div className="flex items-start gap-4">
          <span className="text-sm text-gray-500 w-[120px] shrink-0">
            {new Date(activity.createdAt).toLocaleString()}
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="text-gray-700">{getActivityMessage()}</span>
              {activity.newStatus && (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full uppercase font-medium ${
                    activity.newStatus === "REJECTED"
                      ? "bg-red-100 text-red-700"
                      : activity.newStatus === "APPROVED"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}>
                  {activity.newStatus}
                </span>
              )}
            </div>
            {getActivityDetails()}
          </div>
        </div>
      </div>
    );
  };

  const renderRejectionDetails = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-800">
          Rejection Details
        </h2>
        <button
          onClick={() => setShowApproveForm(prev => !prev)}
          disabled={isChangingToApprove}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2
            ${
              showApproveForm
                ? "text-gray-700 bg-gray-100 hover:bg-gray-200"
                : "text-white bg-[#5652B0] hover:bg-[#4A47A3] shadow-sm hover:shadow"
            }`}>
          {isChangingToApprove ? (
            <>
              <CircularProgress
                size={16}
                sx={{ color: showApproveForm ? "inherit" : "white" }}
              />
              <span>Processing...</span>
            </>
          ) : showApproveForm ? (
            <>
              <XCircle size={18} />
              <span>Cancel</span>
            </>
          ) : (
            <>
              <CheckCircle2 size={18} />
              <span>Change to Approve</span>
            </>
          )}
        </button>
      </div>
      <div className="p-6 space-y-6">
        {showApproveForm ? (
          // Show the approval form
          <div className="space-y-6">
            {/* Credit Limit and Order Validation */}
            <div className="grid grid-cols-2 gap-6">
              {/* Credit Limit */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Credit Limit
                </h4>
                <TextField
                  label="Set Credit Limit"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={creditLimit}
                  onChange={e => setCreditLimit(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </div>

              {/* Order Validation */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Order Validation
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Checkbox
                      checked={useDefaultOrderValidationSettings}
                      onChange={handleDefaultSettingsChange}
                      size="small"
                      sx={{
                        color: "#5652B0",
                        "&.Mui-checked": { color: "#5652B0" },
                      }}
                    />
                    <span className="text-sm">
                      Use company default settings
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <RadioGroup
                      value={orderValidation.validationCondition}
                      onChange={e =>
                        setOrderValidation(prev => ({
                          ...prev,
                          validationCondition: e.target.value,
                        }))
                      }
                      row
                      size="small"
                      disabled={useDefaultOrderValidationSettings}>
                      <FormControlLabel
                        value="ANY_ONE"
                        control={
                          <Radio
                            size="small"
                            disabled={useDefaultOrderValidationSettings}
                            sx={{
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                        }
                        label={<span className="text-sm">Any One</span>}
                      />
                      <FormControlLabel
                        value="BOTH"
                        control={
                          <Radio
                            size="small"
                            disabled={useDefaultOrderValidationSettings}
                            sx={{
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                        }
                        label={<span className="text-sm">Both</span>}
                      />
                    </RadioGroup>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <TextField
                      label="MOQ"
                      variant="outlined"
                      size="small"
                      type="number"
                      value={
                        useDefaultOrderValidationSettings
                          ? vendorStoreData?.data?.store?.orderValidation
                              ?.minOrderQty
                          : orderValidation.minOrderQty
                      }
                      onChange={e =>
                        setOrderValidation(prev => ({
                          ...prev,
                          minOrderQty: e.target.value,
                        }))
                      }
                      disabled={useDefaultOrderValidationSettings}
                      fullWidth
                      InputProps={{
                        readOnly: useDefaultOrderValidationSettings,
                      }}
                    />
                    <TextField
                      label="MOV"
                      variant="outlined"
                      size="small"
                      type="number"
                      value={
                        useDefaultOrderValidationSettings
                          ? vendorStoreData?.data?.store?.orderValidation
                              ?.minOrderValue
                          : orderValidation.minOrderValue
                      }
                      onChange={e =>
                        setOrderValidation(prev => ({
                          ...prev,
                          minOrderValue: e.target.value,
                        }))
                      }
                      disabled={useDefaultOrderValidationSettings}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                        readOnly: useDefaultOrderValidationSettings,
                      }}
                      fullWidth
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Brand Visibility */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Brand Visibility
              </h4>
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-x-4">
                    <div className="flex items-center">
                      <Checkbox
                        checked={selectAllBrands}
                        onChange={handleSelectAllBrands}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": { color: "#5652B0" },
                        }}
                      />
                      <span className="text-sm font-medium">
                        Select all brands
                      </span>
                    </div>
                    {selectedBrands.length > 0 && (
                      <button
                        onClick={handleUnselectAllBrands}
                        className="text-sm border border-[#5652B0] text-[#5652B0] px-3 py-1 rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                        Unselect All
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-x-4">
                    <div className="flex gap-x-3">
                      {["ALL", "SELECTED", "UNSELECTED"].map(filter => (
                        <button
                          key={filter}
                          onClick={() => setBrandFilter(filter)}
                          className={`text-sm font-medium transition-colors ${
                            brandFilter === filter
                              ? "text-[#5652B0] underline"
                              : "text-gray-600 hover:text-[#5652B0]"
                          }`}>
                          View {filter.toLowerCase()}
                        </button>
                      ))}
                    </div>

                    <div className="relative">
                      <SearchIcon
                        className="absolute left-3 top-1/2 transform -translate-y-1/2"
                        size={16}
                        color="#83899F"
                      />
                      <input
                        type="text"
                        placeholder="Search brands..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-[200px] h-9 pl-9 pr-8 rounded-lg border text-sm focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] outline-none"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full">
                          <CircleX size={14} color="#83899F" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 max-h-[200px] overflow-y-auto p-3 bg-gray-50 rounded-lg">
                  {fetchBrandsLoading ? (
                    <div className="col-span-4 flex items-center justify-center h-[100px]">
                      <CircularProgress size={32} sx={{ color: "#5652B0" }} />
                    </div>
                  ) : getFilteredBrands().isEmpty ? (
                    <div className="col-span-4 flex items-center justify-center h-[100px] text-gray-500">
                      No brands found
                    </div>
                  ) : (
                    getFilteredBrands().brands.map(brand => (
                      <BrandCheckbox
                        key={brand._id}
                        id={brand._id}
                        label={brand.name}
                        checked={selectedBrands.includes(brand._id)}
                        onChange={() => handleBrandSelection(brand._id)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Additional Notes
              </h4>
              <TextField
                multiline
                rows={2}
                placeholder="Add any additional notes or comments..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                fullWidth
                size="small"
              />
            </div>

            <button
              onClick={handleChangeToApprove}
              disabled={isChangingToApprove}
              className={`w-full px-4 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg font-medium transition-colors flex items-center justify-center gap-2
                ${
                  isChangingToApprove
                    ? "opacity-50 cursor-not-allowed"
                    : "shadow-sm hover:shadow"
                }`}>
              {isChangingToApprove ? (
                <>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  <span>Approving Request...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} />
                  <span>Approve Request</span>
                </>
              )}
            </button>
          </div>
        ) : (
          // Show rejection details
          <>
            {/* Rejection Reason */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Rejection Reason
              </h4>
              <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                {requestData?.data?.customerRequest?.rejectionReason || "-"}
              </p>
            </div>

            {/* Reconnection Status */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Reconnection Status
              </h4>
              {requestData?.data?.customerRequest?.reconnectionAllowed ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                    <CheckCircle2 size={18} />
                    <span className="text-sm">
                      Customer is allowed to submit a new request
                    </span>
                  </div>

                  {/* Cooldown Period Info */}
                  <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-blue-700">
                      Waiting Period
                    </p>
                    <p className="text-sm text-blue-600">
                      Customer can submit a new request after{" "}
                      {new Date(
                        requestData?.data?.customerRequest?.cooldownPeriod,
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700 bg-red-50 px-3 py-2 rounded-lg">
                  <XCircle size={18} />
                  <span className="text-sm">
                    Customer is not allowed to submit a new request
                  </span>
                </div>
              )}
            </div>

            {/* Rejection Time and By */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Rejected On
                </h4>
                <p className="text-gray-600">
                  {new Date(
                    requestData?.data?.customerRequest?.processedAt,
                  ).toLocaleString()}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Rejected By
                </h4>
                <p className="text-gray-600">
                  {requestData?.data?.customerRequest?.processedBy?.fullName ||
                    "-"}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Implement handleApprove function
  const handleApprove = async () => {
    try {
      const approvalDetails = {
        creditLimit: Number(creditLimit),
        orderValidation: {
          validationType: useDefaultOrderValidationSettings
            ? "COMPANY_SETTINGS"
            : "USER_SPECIFIC",
          ...(useDefaultOrderValidationSettings
            ? {}
            : {
                minOrderQty: Number(orderValidation.minOrderQty),
                minOrderValue: Number(orderValidation.minOrderValue),
                validationCondition: orderValidation.validationCondition,
              }),
        },
        brandVisibility: {
          visibilityType: selectAllBrands
            ? "ALL_COMPANY_BRANDS"
            : "USER_SPECIFIC",
          removedBrands: selectAllBrands
            ? []
            : brandsResponseData?.data?.brands
                .filter(brand => !selectedBrands.includes(brand._id))
                .map(brand => brand._id),
        },
      };

      await approveRequest({
        customerId: requestData?.data?.customerRequest?.customerStore?._id,
        approvalDetails,
        additionalNotes: notes,
      }).unwrap();

      toast.success("Request approved successfully");
      navigate(-1);
    } catch (error) {
      toast.error(error.data?.message || "Failed to approve request");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <ArrowLeft size={20} />
                </button>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold text-gray-800">
                    Customer Request Details
                  </h1>
                  {getStatusBadge(requestData?.data?.customerRequest?.status)}
                  {requestData?.data?.customerRequest?.requestType && (
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium 
                      ${
                        requestData.data.customerRequest.requestType ===
                        "RECONNECTION"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-blue-100 text-blue-700"
                      }`}>
                      {requestData.data.customerRequest.requestType ===
                      "RECONNECTION"
                        ? "Reconnection Request"
                        : "New Connection"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {renderCustomerInfo()}

          {/* Show different sections based on status */}
          {requestData?.data?.customerRequest?.status === "PENDING" &&
            renderPendingActions()}

          {requestData?.data?.customerRequest?.status === "REJECTED" &&
            renderRejectionDetails()}

          {/* Activity Log */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">
                Activity Log
              </h2>
            </div>
            <div>
              {requestData?.data?.recentActivities?.map(activity =>
                formatActivityLog(activity),
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRequestDetails;
